/** @jsxImportSource react */
'use client';

import Image from 'next/image';

import { motion } from 'motion/react';

import { Eyebrow, Heading, Section, Subheading } from './section';

// Single-screenshot product preview block. Sits between the
// outcomes-pitch sections and the social-proof sections on the home
// page to break up an otherwise text-heavy middle.
//
// Tweaks via props if we ever need a second instance with a different
// screenshot; defaults match the home-page placement.
interface Props {
  eyebrow?: string;
  title?: React.ReactNode;
  subhead?: string;
  caption?: string;
  src?: string;
  alt?: string;
}

export function ProductPreview({
  alt = 'OnArk admin: branding settings panel with a live class preview pane on the right',
  caption = 'The OnArk admin — branding panel with the live class preview pane.',
  eyebrow = 'Inside the admin',
  src = '/screenshots/admin/feature-customizations.png',
  subhead = "Your logo. Your colors. Your copy. OnArk's brand stays out of the way so every class looks like it's coming from your church — because it is.",
  title = (
    <>
      Branded for your church.{' '}
      <span className="italic [font-family:var(--font-serif)] font-light">
        Built for your team.
      </span>
    </>
  ),
}: Props) {
  return (
    <Section size="md" tone="mist">
      <motion.div
        className="flex flex-col gap-12 lg:gap-16"
        initial={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        viewport={{ margin: '-100px', once: true }}
        whileInView={{ opacity: 1, y: 0 }}
      >
        <div className="flex max-w-[720px] flex-col gap-5">
          <Eyebrow>{eyebrow}</Eyebrow>
          <Heading size="lg">{title}</Heading>
          {subhead && (
            <Subheading className="text-[color:var(--color-gray-dark)]">
              {subhead}
            </Subheading>
          )}
        </div>

        <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-3">
          <div className="overflow-hidden rounded-[20px] bg-white shadow-[0_24px_60px_-20px_rgba(15,11,26,0.18)] ring-1 ring-[color:var(--color-border-soft)]">
            <Image
              alt={alt}
              className="h-auto w-full"
              height={1644}
              src={src}
              width={2984}
            />
          </div>
          {caption && (
            <p className="text-center text-[12px] text-[color:var(--color-gray-med)]">
              {caption}
            </p>
          )}
        </div>
      </motion.div>
    </Section>
  );
}
